import React, { JSX, MouseEvent, useEffect, useRef, useState, } from 'react';
import { useRouter } from 'next/router';
import { gql, rawRequest } from 'graphql-request';
import LocationHero from './LocationHero';
import LocationBio from './LocationBio';
import LocationProviders from './LocationProviders';
import LocationActionWidget from './LocationActionWidget';
import LocationWidget from './LocationWidget';
import LocationHours from './LocationHours';
import ActionBar from '../shared/ActionBar';
import Sidebar from '../shared/Sidebar';
import Insurance from '../shared/Insurance';

import getOsfViewportSize from 'assets/utils/getOsfViewportSize';
import { getDeliveryKey, getDeliveryUrl } from 'lib/util';
import {
  LocationFields,
  Provider,
} from 'components/types/LocationTypes';

type LocationAnchorItem = {
  id: string;
  label: string;
  isVisible: boolean;
};

type LocationAnchorBarProps = {
  items: LocationAnchorItem[];
  onItemClick?: (
    sectionId: string
  ) => boolean | void;
  onActiveItemChange?: (
    sectionId: string
  ) => void;
};

const hasTextValue = (value?: string): boolean =>
  Boolean(value?.trim());

const LocationAnchorBar = ({
  items,
  onItemClick,
  onActiveItemChange,
}: LocationAnchorBarProps): JSX.Element | null => {
  const visibleItems = items.filter(item => item.isVisible);
  const visibleItemIds = visibleItems.map(item => item.id);
  const visibleItemKey = visibleItemIds.join('|');

  const anchorBarContainerRef =
    useRef<HTMLDivElement>(null);

  const [activeId, setActiveId] = useState(
    visibleItemIds[0] || ''
  );

  const activeIdRef = useRef(
    visibleItemIds[0] || ''
  );

  const onActiveItemChangeRef =
    useRef(onActiveItemChange);

  useEffect(() => {
    onActiveItemChangeRef.current =
      onActiveItemChange;
  }, [onActiveItemChange]);

  useEffect(() => {
    const itemIds = visibleItemKey
      ? visibleItemKey.split('|')
      : [];

    if (itemIds.length === 0) {
      activeIdRef.current = '';
      setActiveId('');
      return;
    }

    setActiveId(currentId => {
      const nextActiveId =
        itemIds.includes(currentId)
          ? currentId
          : itemIds[0];

      activeIdRef.current =
        nextActiveId;

      return nextActiveId;
    });

    let animationFrameId: number | null = null;

    const updateActiveIdFromScroll = (
      sectionId: string
    ): void => {
      if (
        activeIdRef.current === sectionId
      ) {
        return;
      }

      activeIdRef.current = sectionId;
      setActiveId(sectionId);

      onActiveItemChangeRef.current?.(
        sectionId
      );
    };

    const updateActiveSection = (): void => {
      const anchorBarContainer =
        anchorBarContainerRef.current;

      if (!anchorBarContainer) {
        return;
      }

      const computedStyles = window.getComputedStyle(
        anchorBarContainer
      );

      const stickyTop =
        Number.parseFloat(computedStyles.top) || 0;

      const activationLine =
        stickyTop +
        anchorBarContainer.offsetHeight +
        10;

      const hasReachedPageBottom =
        window.scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 2;

      if (hasReachedPageBottom) {
        const lastVisibleId =
          itemIds[itemIds.length - 1];

        updateActiveIdFromScroll(
          lastVisibleId
        );

        return;
      }

      let currentSectionId = itemIds[0];

      itemIds.forEach(sectionId => {
        const section =
          document.getElementById(sectionId);

        if (!section) {
          return;
        }

        const sectionTop =
          section.getBoundingClientRect().top;

        if (sectionTop <= activationLine) {
          currentSectionId = sectionId;
        }
      });

      updateActiveIdFromScroll(
        currentSectionId
      );
    };

    const handleViewportChange = (): void => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId =
        window.requestAnimationFrame(() => {
          updateActiveSection();
          animationFrameId = null;
        });
    };

    updateActiveSection();

    window.addEventListener(
      'scroll',
      handleViewportChange,
      {
        passive: true,
      }
    );

    window.addEventListener(
      'resize',
      handleViewportChange
    );

    window.addEventListener(
      'hashchange',
      handleViewportChange
    );

    return () => {
      window.removeEventListener(
        'scroll',
        handleViewportChange
      );

      window.removeEventListener(
        'resize',
        handleViewportChange
      );

      window.removeEventListener(
        'hashchange',
        handleViewportChange
      );

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(
          animationFrameId
        );
      }
    };
  }, [visibleItemKey]);

  const handleAnchorClick = (
    event: MouseEvent<HTMLAnchorElement>,
    sectionId: string
  ): void => {
    event.preventDefault();

    const shouldPreventNavigation =
      onItemClick?.(sectionId) === true;

    if (shouldPreventNavigation) {
      return;
    }

    const targetSection =
      document.getElementById(sectionId);

    const anchorBarContainer =
      anchorBarContainerRef.current;

    if (!targetSection || !anchorBarContainer) {
      return;
    }

    const computedStyles = window.getComputedStyle(
      anchorBarContainer
    );

    const stickyTop =
      Number.parseFloat(computedStyles.top) || 0;

    const scrollOffset =
      stickyTop +
      anchorBarContainer.offsetHeight +
      8;

    const targetScrollPosition =
      window.scrollY +
      targetSection.getBoundingClientRect().top -
      scrollOffset;

    activeIdRef.current = sectionId;
    setActiveId(sectionId);

    window.history.replaceState(
      null,
      '',
      `#${sectionId}`
    );

    window.scrollTo({
      top: Math.max(targetScrollPosition, 0),
      behavior: 'smooth',
    });
  };

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <div
      ref={anchorBarContainerRef}
      className="location-anchor-bar-container"
    >
      <nav
        className="location-anchor-bar"
        aria-label="Location page sections"
      >
        <ul className="location-anchor-bar-list">
          {visibleItems.map(item => {
            const isActive = activeId === item.id;

            return (
              <li
                key={item.id}
                className="location-anchor-bar-item"
              >
                <a
                  className={`location-anchor-bar-link ${
                    isActive ? 'is-active' : ''
                  }`}
                  href={`#${item.id}`}
                  aria-current={
                    isActive ? 'location' : undefined
                  }
                  onClick={event =>
                    handleAnchorClick(event, item.id)
                  }
                >
                  {item.label}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

const LocationProfile = (): JSX.Element => {
  const router = useRouter();

  const [fields, setFields] = useState(
    {} as LocationFields
  );

  const [locationProviders, setLocationProviders] =
    useState<Array<Provider>>([]);

  const [hasProviders, setHasProviders,] =
    useState(false);

  const [size, setSize] = useState('sm');

  const scheduleAnchorActiveRef =
    useRef(false);

  const routerQueryPath = router.query
    .path as Array<string>;

  const locationItemName = routerQueryPath?.length
    ? routerQueryPath[
        routerQueryPath.length - 1
      ]
    : routerQueryPath?.[1];

  const locationId = locationItemName?.substring(
    locationItemName.lastIndexOf('-') + 1,
    locationItemName.length
  );

  const deliveryKey = getDeliveryKey();
  const endpoint = getDeliveryUrl();

  const locationItemId = fields?.id
    ? `{${fields.id.substring(
        0,
        8
      )}-${fields.id.substring(
        8,
        12
      )}-${fields.id.substring(
        12,
        16
      )}-${fields.id.substring(
        16,
        20
      )}-${fields.id.substring(20)}}`
    : '';

  const hasLoadedLocation = Boolean(fields?.id);

  const locationName =
    fields?.LocationName?.value || '';

  const normalizedLocationName =
    locationName.toLowerCase();

  const isPharmacyLocation =
    normalizedLocationName.includes('pharmacy');

  const isUrgentCareLocation =
    normalizedLocationName.includes(
      'oncall urgent'
    );

  const isPromptCareLocation =
    normalizedLocationName.includes('promptcare');

  const isCompleteCareLocation =
    normalizedLocationName.includes(
      'completecare'
    );

  const isMobile =
    size === 'xs' ||
    size === 'sm' ||
    size === 'md';

  const isDesktop =
    size === 'lg' ||
    size === 'xl';

  const hasLocationContent =
    hasLoadedLocation &&
    (hasTextValue(fields?.About?.value) ||
      hasTextValue(fields?.Parking?.value) ||
      hasTextValue(
        fields?.PrepareForYourFirstVisit?.value
      ) ||
      hasTextValue(
        fields?.CommittedToExcellence?.value
      ));

  const hasServicesContent =
    hasLoadedLocation &&
    hasTextValue(
      fields?.ServicesOffered?.value
    );

  const hasHoursContent =
    hasLoadedLocation &&
    hasTextValue(
      fields?.HoursOfOperation?.value
    );

  const hasInsuranceContent =
    hasLoadedLocation &&
    fields?.HideInsuranceAccepted?.value !== '1';


  const hasSchedulingActions =
    hasLoadedLocation &&
    Boolean(
      hasTextValue(
        fields?.BookOnlineOverrideUrl?.value
      ) ||
        isUrgentCareLocation ||
        isPromptCareLocation ||
        isCompleteCareLocation
    );

  const locationAnchorItems: LocationAnchorItem[] = [
      {
        id: 'location-overview',
        label: 'Location',
        isVisible: hasLocationContent,
      },
      {
        id: 'location-services',
        label: 'Services',
        isVisible: hasServicesContent,
      },
      {
        id: 'location-hours',
        label: 'Hours',
        isVisible: hasHoursContent,
      },
      {
        id: 'location-insurance',
        label: 'Insurance',
        isVisible: hasInsuranceContent,
      },
      {
        id: 'location-providers',
        label: 'Providers',
        isVisible: hasProviders,
      },
      {
        id: 'location-schedule',
        label: 'Schedule',
        isVisible: hasSchedulingActions,
      },
    ];

  const locationQuery = gql`
    query ($locationId: String!) {
      search(
        where: {
          AND: [
            {
              name: "_path"
              value: "{F7971D27-B938-4280-A061-E92A81AE8C99}"
              operator: CONTAINS
            }
            {
              name: "LocationID"
              value: $locationId
              operator: CONTAINS
            }
          ]
        }
        first: 1
      ) {
        results {
          id
          name

          About: field(name: "About") {
            value: value
          }

          Parking: field(name: "Parking") {
            value: value
          }

          PrepareForYourFirstVisit: field(
            name: "PrepareForYourFirstVisit"
          ) {
            value: value
          }

          CommittedToExcellence: field(
            name: "CommittedToExcellence"
          ) {
            value: value
          }

          BookOnlineOverrideUrl: field(
            name: "BookOnlineOverrideUrl"
          ) {
            value: value
          }

          City: field(name: "City") {
            value: value
          }

          EpicDeptId: field(name: "EpicDeptId") {
            value: value
          }

          EpicProviderId: field(
            name: "EpicProviderid"
          ) {
            value: value
          }

          HideInsuranceAccepted: field(
            name: "HideInsuranceAccepted"
          ) {
            value: value
          }

          HoursOfOperation: field(
            name: "HoursOfOperation"
          ) {
            value: value
          }

          HoursOfOperationNotes: field(
            name: "HoursOfOperationNotes"
          ) {
            value: value
          }

          Latitude: field(name: "Latitude") {
            value: value
          }

          LocationImage: field(
            name: "LocationImage"
          ) {
            value
          }

          LocationName: field(
            name: "LocationName"
          ) {
            value: value
          }

          LocationTypes: field(
            name: "LocationTypes"
          ) {
            value: jsonValue
          }

          Longitude: field(name: "Longitude") {
            value: value
          }

          Phone: field(name: "Phone") {
            value: value
          }

          Providers: field(name: "Providers") {
            value: jsonValue
          }

          ServicesOffered: field(
            name: "ServicesOffered"
          ) {
            value: value
          }

          State: field(name: "State") {
            value: value
          }

          Street1: field(name: "Street1") {
            value: value
          }

          Street2: field(name: "Street2") {
            value: value
          }

          Suite: field(name: "Suite") {
            value: value
          }

          Zip: field(name: "Zip") {
            value: value
          }
        }
      }
    }
  `;

  const locationProvidersQuery = gql`
    query ($locationItemId: String!) {
      search(
        where: {
          AND: [
            {
              name: "_path"
              value: "{6A68FA22-BE0C-46BB-912C-22F1F83E28DC}"
              operator: CONTAINS
            }
            {
              name: "Locations"
              value: $locationItemId
              operator: CONTAINS
            }
          ]
        }
        first: 1000
      ) {
        results {
          name

          FirstName: field(name: "FirstName") {
            value
          }

          LastName: field(name: "LastName") {
            value
          }

          NPI: field(name: "NPI") {
            value
          }

          Degrees: field(name: "Degrees") {
            value
          }

          ExternalID: field(name: "ExternalID") {
            value
          }

          FullName: field(name: "FullName") {
            value
          }

          Gender: field(name: "Gender") {
            value
          }

          ProviderImage: field(
            name: "ProviderImage"
          ) {
            value: value
          }

          Specialty: field(name: "Specialty") {
            value
          }
          
          ClinicalFocus: field(name: "Clinical Focus") {
            value: jsonValue
          }
        }
      }
    }
  `;

  const headers = {
    sc_apikey: deliveryKey,
  };

  const getLocation = async (): Promise<void> => {
    if (!locationId) {
      return;
    }

    try {
      const { data } = await rawRequest(
        endpoint,
        locationQuery,
        { locationId },
        headers
      );

      const location = data?.search?.results?.[0];

      if (location) {
        setFields(location as LocationFields);
      }
    } catch (error) {
      console.error(
        'Error fetching location:',
        error
      );
    }
  };

  const getLocationProviders =
    async (): Promise<void> => {
      if (!locationItemId) {
        return;
      }

      try {
        const { data } = await rawRequest(
          endpoint,
          locationProvidersQuery,
          { locationItemId },
          headers
        );

        const providers =
          data?.search?.results || [];

        if (providers.length > 0) {
          setHasProviders(true);

          setLocationProviders(
            providers as Array<Provider>
          );

          return;
        }

        setHasProviders(false);
        setLocationProviders([]);
      } catch (error) {
        console.error(
          'Error fetching location providers:',
          error
        );

        setHasProviders(false);
        setLocationProviders([]);
      }
    };

  useEffect(() => {
    getLocation();
  }, [locationId]);

  useEffect(() => {
    getLocationProviders();
  }, [locationItemId]);

  useEffect(() => {
    const resize = (): void => {
      setSize(getOsfViewportSize());
    };

    resize();

    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener(
        'resize',
        resize
      );
    };
  }, []);

  const openScheduleDrawer =
    (): void => {
      if (!isMobile) {
        return;
      }

      const scheduleSection =
        document.getElementById(
          'location-schedule'
        );

      const scheduleSidebar =
        scheduleSection?.querySelector<HTMLElement>(
          '.sidebar.location'
        );

      const scheduleHeader =
        scheduleSidebar?.querySelector<HTMLElement>(
          '.header'
        );

      const isScheduleOpen =
        scheduleSidebar?.classList.contains(
          'open'
        );

      if (
        scheduleHeader &&
        !isScheduleOpen
      ) {
        scheduleHeader.click();
      }
    };

  const closeScheduleDrawer =
    (): void => {
      if (!isMobile) {
        return;
      }

    const scheduleSection =
      document.getElementById(
        'location-schedule'
      );

    const scheduleSidebar =
      scheduleSection?.querySelector<HTMLElement>(
        '.sidebar.location'
      );

    const scheduleHeader =
      scheduleSidebar?.querySelector<HTMLElement>(
        '.header'
      );

    const isScheduleOpen =
      scheduleSidebar?.classList.contains(
        'open'
      );

      if (
        scheduleHeader &&
        isScheduleOpen
      ) {
        scheduleHeader.click();
      }
    };

  const handleAnchorItemClick = (
    sectionId: string
  ): boolean => {
    if (
      sectionId !==
        'location-schedule' ||
      !isMobile
    ) {
      return false;
    }

    openScheduleDrawer();

    // Prevent hash update and page scrolling.
    return true;
  };

  const handleActiveAnchorChange = (
    sectionId: string
  ): void => {
    if (!isMobile) {
      return;
    }

    if (
      sectionId ===
      'location-schedule'
    ) {
      scheduleAnchorActiveRef.current =
        true;

      openScheduleDrawer();

      return;
    }

    if (
      scheduleAnchorActiveRef.current
    ) {
      scheduleAnchorActiveRef.current =
        false;

      closeScheduleDrawer();
    }
  };

  const locationActionWidget = (
    <LocationActionWidget
      mammogram={
        fields?.BookOnlineOverrideUrl?.value
      }
      pharmacy={isPharmacyLocation}
      urgent={isUrgentCareLocation}
      prompt={isPromptCareLocation}
      completeCare={isCompleteCareLocation}
      deptId={fields?.EpicDeptId?.value}
      providerId={fields?.EpicProviderId?.value}
      locationName={
        fields?.LocationName?.value
      }
      street1={fields?.Street1?.value}
      street2={fields?.Street2?.value}
      suite={fields?.Suite?.value}
      city={fields?.City?.value}
      state={fields?.State?.value}
      zip={fields?.Zip?.value}
      phone={fields?.Phone?.value}
      hours={
        fields?.HoursOfOperation?.value
      }
    />
  );

  return (
    <div className="location-profile">
      {fields?.id && (
        <LocationHero
          img={fields?.LocationImage}
          locationName={
            fields?.LocationName?.value
          }
          locationTypes={
            fields?.LocationTypes
          }
        />
      )}

      <div className="overhauled">
        <div className="jss-inner-container">
          <div className="profile-layout">
            <div className="left-col">
              <LocationAnchorBar
                items={locationAnchorItems}
                onItemClick={
                  handleAnchorItemClick
                }
                onActiveItemChange={
                  handleActiveAnchorChange
                }
              />

              {isMobile && (
                <LocationWidget
                  img={fields?.LocationImage}
                  locationName={
                    fields?.LocationName?.value
                  }
                  street1={
                    fields?.Street1?.value
                  }
                  street2={
                    fields?.Street2?.value
                  }
                  suite={fields?.Suite?.value}
                  city={fields?.City?.value}
                  state={fields?.State?.value}
                  zip={fields?.Zip?.value}
                  phone={fields?.Phone?.value}
                  latitude={
                    fields?.Latitude?.value
                  }
                  longitude={
                    fields?.Longitude?.value
                  }
                />
              )}

              {(hasLocationContent ||
                hasServicesContent) && (
                <LocationBio
                  locationSectionId="location-overview"
                  servicesSectionId="location-services"
                  About={fields?.About}
                  Parking={fields?.Parking}
                  PrepareForYourFirstVisit={
                    fields?.PrepareForYourFirstVisit
                  }
                  CommittedToExcellence={
                    fields?.CommittedToExcellence
                  }
                  LocationName={
                    fields?.LocationName
                  }
                  LocationTypes={
                    fields?.LocationTypes
                  }
                  ServicesOffered={
                    fields?.ServicesOffered
                  }
                />
              )}

              {hasHoursContent && (
                <section
                  id="location-hours"
                  className="location-anchor-target"
                  aria-label="Hours"
                >
                  <LocationHours
                    hours={
                      fields.HoursOfOperation.value
                    }
                    hoursNotes={
                      fields.HoursOfOperationNotes?.value
                    }
                  />
                </section>
              )}

              {hasInsuranceContent && (
                <section
                  id="location-insurance"
                  className="location-anchor-target"
                  aria-label="Insurance"
                >
                  <Insurance />
                </section>
              )}

              {hasProviders && (
                <section
                  id="location-providers"
                  className="location-anchor-target"
                  aria-label="Providers"
                >
                  <LocationProviders
                    providers={locationProviders}
                  />
                </section>
              )}

              {isDesktop &&
                hasSchedulingActions && (
                  <section
                    id="location-schedule"
                    className="location-anchor-target"
                    aria-label="Schedule"
                  >
                    {locationActionWidget}
                  </section>
                )}

              <ActionBar print copy />

              {isMobile &&
                hasSchedulingActions && (
                  <section
                    id="location-schedule"
                    className="location-anchor-target mobile-schedule-target"
                    aria-label="Schedule"
                  >
                    {locationActionWidget}
                  </section>
                )}
            </div>

            <div className="right-col">
              {isDesktop && (
                <Sidebar
                  header="Location"
                  pageType="location"
                  customHeader="osf-display-h2"
                  hideLocationHeader
                >
                  <LocationWidget
                    img={fields?.LocationImage}
                    locationName={
                      fields?.LocationName?.value
                    }
                    street1={fields?.Street1?.value}
                    street2={fields?.Street2?.value}
                    suite={fields?.Suite?.value}
                    city={fields?.City?.value}
                    state={fields?.State?.value}
                    zip={fields?.Zip?.value}
                    phone={fields?.Phone?.value}
                    latitude={fields?.Latitude?.value}
                    longitude={fields?.Longitude?.value}
                  />
                </Sidebar>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationProfile;